import React, { useState, useMemo } from 'react';
import { Formik, Field as FormikField } from 'formik';
import * as Yup from 'yup';
import {
  Button as UiButton,
  Typography,
  Input,
  Select,
} from '@deeptrue-frontend/ui';
import styled from 'styled-components/macro';
import { useFormatMessage } from 'components/IntlProvider';
import { FormatMessage } from 'components/IntlProvider/hooks/hooks';
import { ProjectType } from 'store/modules/projects/projects.types';
import DataCollectionLinkModal from 'components/Project/CollectData/ManualDataCollection/DataCollectionLinkModal';
import { ProjectStatus } from 'consts/statuses';

const getValidationSchema = (t: FormatMessage) =>
  Yup.object().shape<FormValues>({
    count: Yup.number()
      .required(t('errors.fields.required', 'Field is required'))
      .min(1, t('errors.fields.minValue', 'Put value from 1 - 10000'))
      .max(10000, t('errors.fields.minValue', 'Put value from 1 - 10000')),
    country: Yup.string().required(
      t('errors.fields.required', 'Field is required')
    ),
  });

export const StyledInput = styled(Input)`
  width: 5rem;
  border: 1px solid #b5b5b0;
  background-color: #fff;
  outline: none;
  height: 2.5rem;
  -webkit-appearance: none;
  ::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  ::-webkit-outer-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  -moz-appearance: textfield;
`;

const StyledButton = styled(UiButton)`
  margin-top: 1rem;
  font-size: 1rem;
`;

const StyledLabel = styled(Typography)`
  font-size: 1.125rem;
  margin-bottom: 0.875rem;
  font-weight: 500;
`;

const StyledInputWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  margin: 1rem 0;
`;

export interface ParamPair {
  [k: string]: string;
}

export interface FormValues {
  country: string;
  count: number;
  parameters?: ParamPair | ParamPair[];
}

interface Props {
  initialValues: FormValues;
  onSubmit: (values: FormValues) => void;
  isDisabled?: boolean;
  submitError?: string;
  project: ProjectType;
}

const DownloadSurveyUrls = ({
  initialValues,
  project,
  onSubmit,
  submitError,
  isDisabled,
}: Props) => {
  const t = useFormatMessage();
  const [isModalVisible, setModalVisibility] = useState(false);

  const languageOptions = useMemo(
    () => [
      {
        value: 'gb',
        label: 'UK',
      },
      {
        value: 'de',
        label: 'DE',
      },
      {
        value: 'fr',
        label: 'FR',
      },
      {
        value: 'it',
        label: 'IT',
      },
      {
        value: 'ch',
        label: 'CH',
      },
      {
        value: 'at',
        label: 'AT',
      },
      {
        value: 'us',
        label: 'US',
      },
      {
        value: 'XX',
        label: 'GLOBAL',
      },
    ],
    []
  );

  const hasDataCollectionStarted =
    project.status.value === ProjectStatus.COLLECTING_DATA;

  const formatParameters = (params: ParamPair[]) => {
    const cleanParams: ParamPair[] = params?.filter(
      (p) => p.key.length && p.value.length
    );

    const finalParams = cleanParams?.reduce((newParam, oldParam) => {
      newParam[oldParam.key] = oldParam.value;
      return newParam;
    }, {});

    return finalParams;
  };

  const onSubmitConfirm = (
    values: FormValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    onSubmit({
      ...values,
      parameters: formatParameters(values.parameters as ParamPair[]),
    });

    resetForm();
    setModalVisibility(false);
  };

  const { final_fee, voucher } = project;

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={onSubmitConfirm}
      validationSchema={getValidationSchema(t)}
    >
      {({ handleSubmit, errors, touched, isSubmitting, setFieldValue }) => {
        return (
          <form>
            {submitError && (
              <Typography variant="copy3" color="punch">
                {submitError}
              </Typography>
            )}
            <StyledInputWrapper>
              <StyledLabel>
                {t('pages.project.survey.amountOfLinks', 'Amount of links')}
              </StyledLabel>
              <FormikField
                name="count"
                type="number"
                hasError={touched.count && errors.count}
                helperText={touched.count && errors.count}
                as={StyledInput}
                data-testid="count"
                min="1"
                max="10000"
              />
            </StyledInputWrapper>
            <StyledInputWrapper>
              <StyledLabel>
                {t(
                  'pages.project.survey.countryOfRecipients',
                  'Country of recipients'
                )}
              </StyledLabel>
              <FormikField
                as={Select}
                name="country"
                options={languageOptions}
                hasError={touched.country && errors.country}
                onChange={(name: string, lan: string) =>
                  setFieldValue(name, lan)
                }
                placeholder={t(
                  'pages.project.survey.linksCountry',
                  'Select country code'
                )}
              />
            </StyledInputWrapper>

            <DataCollectionLinkModal
              isVisible={isModalVisible}
              onAcceptTerms={handleSubmit}
              onBackdropClick={() => setModalVisibility(false)}
              onCloseClick={() => setModalVisibility(false)}
              credits={final_fee}
              voucher={voucher}
            />

            <StyledButton
              disabled={isDisabled || isSubmitting}
              data-testid="submit"
              onClick={(e) => {
                if (!hasDataCollectionStarted) {
                  e.preventDefault();
                  setModalVisibility(true);
                } else {
                  e.preventDefault();
                  handleSubmit();
                }
              }}
              isLoading={isSubmitting}
            >
              {t('pages.project.survey.downloadCsv', 'Download links ')}
            </StyledButton>
          </form>
        );
      }}
    </Formik>
  );
};

export default DownloadSurveyUrls;
